/**
 * The five states every screen must define (docs/08-uiux.md §7).
 *
 * The legacy UI answered only the first: it rendered data, and if anything
 * else happened you got a frozen button. These components exist so that
 * shipping a screen without its loading, empty and error states requires
 * deliberately not importing them.
 */

import type { ReactNode } from 'react'
import { AlertTriangle, Inbox, Lock, RefreshCw, WifiOff } from 'lucide-react'
import { Button, Card, Skeleton } from '@/components/ui/primitives'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/utils'

export function PageHeader({
  title,
  description,
  breadcrumb,
  actions,
  className,
  inline = false,
}: {
  title: string
  description?: string
  breadcrumb?: ReactNode
  actions?: ReactNode
  className?: string
  /** The description on the title's line rather than under it. */
  inline?: boolean
}) {
  return (
    <header className={cn('flex flex-wrap items-end justify-between gap-4 pb-6', className)}>
      <div className={cn('min-w-0', inline && 'flex flex-wrap items-baseline gap-x-3')}>
        {breadcrumb && (
          <nav className="mb-1.5 text-[12px] font-medium text-[var(--text-muted)]">{breadcrumb}</nav>
        )}
        <h1 className="text-[24px] font-semibold leading-tight tracking-[-0.02em]">{title}</h1>
        {description && (
          <p
            className={cn(
              'max-w-[70ch] text-[13.5px] text-[var(--text-muted)]',
              inline ? 'truncate' : 'mt-1.5',
            )}
          >
            {description}
          </p>
        )}
      </div>
      {actions && (
        // Capped at the header's width so a long row of actions - the template
        // editor has seven - wraps instead of pushing the page sideways.
        <div className="flex max-w-full shrink-0 flex-wrap items-center gap-2">{actions}</div>
      )}
    </header>
  )
}

/**
 * Skeletons match the shape of what is coming, never a spinner over a blank
 * page — so the layout does not jump when the data lands.
 */
export function LoadingState({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3', className)} aria-busy="true" aria-live="polite">
      <span className="sr-only">Loading</span>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-4">
          <Skeleton className="h-9 w-9 rounded-full" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-24" />
          <Skeleton className="h-4 w-16" />
        </div>
      ))}
    </div>
  )
}

/**
 * An empty pane always explains what goes there and offers the action that
 * creates the first one. Never a blank rectangle.
 */
export function EmptyState({
  title,
  description,
  action,
  icon,
}: {
  title: string
  description: string
  action?: ReactNode
  icon?: ReactNode
}) {
  return (
    <Card variant="glow" className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="icon-chip grid h-12 w-12 place-items-center rounded-full text-[var(--text-muted)]">
        {icon ?? <Inbox className="h-6 w-6" aria-hidden />}
      </div>
      <h2 className="text-[15px] font-semibold">{title}</h2>
      <p className="max-w-[46ch] text-[13px] text-[var(--text-muted)]">{description}</p>
      {action && <div className="mt-2">{action}</div>}
    </Card>
  )
}

/**
 * A human sentence, the request id, and a retry.
 *
 * The request id is the point: it is what turns "it broke" into a log line
 * support can actually find.
 */
export function ErrorState({
  error,
  onRetry,
  title = 'Something went wrong',
}: {
  error: unknown
  onRetry?: () => void
  title?: string
}) {
  const apiError = error instanceof ApiError ? error : null
  const message =
    apiError?.message ??
    (error instanceof Error ? error.message : 'An unexpected error occurred.')

  return (
    <Card variant="glow" className="flex flex-col items-center gap-3 px-6 py-12 text-center">
      <div className="grid h-12 w-12 place-items-center rounded-full bg-[var(--danger-subtle)] text-[var(--danger)]">
        <AlertTriangle className="h-6 w-6" aria-hidden />
      </div>
      <h2 className="text-[15px] font-semibold">{apiError?.title ?? title}</h2>
      <p className="max-w-[52ch] text-[13px] text-[var(--text-muted)]">{message}</p>
      {apiError?.fieldErrors?.length ? (
        <ul className="text-[12px] text-[var(--text-muted)]">
          {apiError.fieldErrors.map((fieldError) => (
            <li key={fieldError.field}>
              {fieldError.field}: {fieldError.message}
            </li>
          ))}
        </ul>
      ) : null}
      {apiError?.requestId && (
        <p className="font-mono text-[11px] text-[var(--text-muted)]">
          Reference {apiError.requestId}
        </p>
      )}
      {onRetry && (
        <Button
          variant="secondary"
          size="sm"
          onClick={onRetry}
          icon={<RefreshCw className="h-3.5 w-3.5" />}
          className="mt-2"
        >
          Try again
        </Button>
      )}
    </Card>
  )
}

/**
 * The sixth state: signed in, but not allowed here.
 *
 * The sidebar hides what a user cannot reach, so the only way to land on a
 * forbidden route is a bookmark, a shared link, a typed address or the back
 * button — all of which are ordinary things people do. Without this they got
 * the page shell, an API call, a 403, and "Something went wrong", which reads
 * like a broken system rather than a closed door.
 *
 * The server checks the same permission again; this only decides what to
 * render (docs/08-uiux.md §12).
 */
export function NoAccessState({ home }: { home?: ReactNode }) {
  return (
    <Card variant="glow" className="flex flex-col items-center gap-3 px-6 py-14 text-center">
      <div className="icon-chip grid h-12 w-12 place-items-center rounded-full text-[var(--text-muted)]">
        <Lock className="h-6 w-6" aria-hidden />
      </div>
      <h1 className="text-[15px] font-semibold">You do not have access to this page</h1>
      <p className="max-w-[48ch] text-[13px] text-[var(--text-muted)]">
        Your role does not include this screen. If you think it should, ask a
        property administrator to change your role.
      </p>
      {home && <div className="mt-2">{home}</div>}
    </Card>
  )
}

/**
 * The degraded state.
 *
 * When the socket drops the app stays usable read-only and says so, rather
 * than looking fine while silently missing every update — which is what the
 * legacy UI did, and why receptionists learned to refresh constantly.
 */
export function ConnectionBanner({ state }: { state: string }) {
  if (state === 'connected') return null

  const reconnecting = state === 'reconnecting' || state === 'connecting'
  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        'flex items-center gap-2 px-4 py-2 text-[13px]',
        reconnecting
          ? 'bg-[var(--warning-subtle)] text-[var(--warning)]'
          : 'bg-[var(--danger-subtle)] text-[var(--danger)]',
      )}
    >
      <WifiOff className="h-4 w-4 shrink-0" aria-hidden />
      {reconnecting
        ? 'Reconnecting… live updates are paused. Nothing has been lost.'
        : 'Not connected. Live updates are off — reload once your connection returns.'}
    </div>
  )
}

/** Confidence as a bar plus the number, because colour alone is not enough. */
export function ConfidenceBadge({ value }: { value: number | null | undefined }) {
  if (value == null) return <span className="text-[12px] text-[var(--text-muted)]">—</span>

  const tone =
    value >= 0.85
      ? 'var(--success)'
      : value >= 0.7
        ? 'var(--warning)'
        : 'var(--danger)'

  return (
    <span className="inline-flex items-center gap-1.5" title={`OCR confidence ${value.toFixed(2)}`}>
      <span className="h-1.5 w-10 overflow-hidden rounded-full bg-[var(--surface-2)]">
        <span
          className="block h-full rounded-full"
          style={{ width: `${value * 100}%`, background: tone }}
        />
      </span>
      <span className="tabular text-[11px] text-[var(--text-muted)]">{value.toFixed(2)}</span>
    </span>
  )
}
